import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { colors as vizColors } from '@tidepool/viz';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import PropTypes from 'prop-types';

import * as actions from '../../redux/actions';

import _ from 'lodash';

import utils from '../../core/utils';
import { validateForm } from '../../core/validation';
import { dateRegex } from '../../core/clinicUtils';
import TextInput from '../../components/elements/TextInput';
import { Box } from 'theme-ui';
import Icon from '../../components/elements/Icon';
import InputMask from 'react-input-mask';

import Container from '../../components/elements/Container';

const styleProps = {
  inputFieldContainer: {
    position: 'relative',
    py: 2,
  },
  visibilityIconContainer: {
    color: vizColors.gray50,
    position: 'absolute',
    right: '12px',
    bottom: '12px',

  },
  visibilityIcon: {
    '&:focus:not(:focus-visible)': { boxShadow: 'none' },
  },
  passwordInputField: {
    width: '100%',
    'input[id="password"]': { padding: 2 },
    'input[id="passwordConfirm"]': { padding: 2 },
  },
  actionButton: {
    minWidth: ['100%', '100%', '160px', '160px'],
  },
  notificationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: 0,
  },
};

const Notification = ({ notification = null }) => {
  if (!notification?.message) return null;

  const type = notification.type || 'alert';
  const className = [
    'simple-form-notification',
    'simple-form-notification-' + type,
    'js-form-notification',
  ].join(' ');

  return <div className={className}>{notification.message}</div>;
};

Notification.propTypes = {
  notification: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
};

const useInferIsTwoStepWorkflow = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  // If there is a restrictedToken in the params, we assume that this is the second step
  // of a flow where the invitation to connect cloud devices came first. Otherwise, we
  // assume that the user was sent directly to this screen.
  const isTwoStepWorkflow = queryParams.has('restrictedTokenId');

  return isTwoStepWorkflow;
};

const VerificationWithPassword = ({
  api,
  fetchingUser,
  user,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { search } = location;

  const isTwoStepWorkflow = useInferIsTwoStepWorkflow();
  const signupKey = utils.getSignupKey(location);
  const signupEmail = utils.getSignupEmail(location);

  const propsNotification = useSelector(state => state.blip.working.verifyingCustodial.notification);
  const working = useSelector(state => state.blip.working.verifyingCustodial.inProgress);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible] = useState(false);
  const [formValues, setFormValues] = useState({
    password: '',
    confirmPassword: '',
    birthday: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [notification, setNotification] = useState(null);

  const handleInputChange = (name, value) => {
    setFormValues(prevState => ({ ...prevState, [name]: value }));
  };

  const validateFormValues = (formValues) => {
    const [year, month, day] = (formValues.birthday || '').split('-');

    const validationMonth = month && String(Number(month) - 1); // validator expects a zero-indexed month
    const birthdayForValidation = { year, month: validationMonth, day };

    const form = [
      { type: 'date', name: 'birthday', label: t('birthday'), value: birthdayForValidation },
      { type: 'password', name: 'password', label: t('password'), value: formValues.password},
      { type: 'confirmPassword', name: 'passwordConfirm', label: t('confirm password'), value: formValues.passwordConfirm, prerequisites: { password: formValues.password } }
    ];
    const validationErrors = validateForm(form, true);

    if (!_.isEmpty(validationErrors)) {
      setValidationErrors(validationErrors);
      setNotification({ type: 'error', message: t('Some entries are invalid.')});
    }

    return validationErrors;
  };

  const handleSkip = () => window?.close();

  const handleSubmit = (evt) => {
    if (evt) evt.preventDefault();

    if (working) return;

    setValidationErrors({});
    setNotification(null);
    const validationErrors = validateFormValues({ ...formValues });

    if (!_.isEmpty(validationErrors)) return;

    const { birthday, password } = formValues;
    dispatch(actions.async.verifyCustodial(api, signupKey, signupEmail, birthday, password));
  };

  const disabled = fetchingUser && !user;

  return (
    <Box sx={{ paddingTop: ['72px', '72px', '86px', '86px'] }}>
      <Container
        title={
          isTwoStepWorkflow
            ? t('Optional: Setup Your Account')
            : t('Claim Your Account')
        }
        subtitle={t('Set a password to access your data from home')}
        variant="mediumBordered"
        p={4}
        pt={3}
        sx={{ flexDirection: ['column', 'column', 'row', 'row'] }}
        actions={[
          (isTwoStepWorkflow && {
            id: 'verificationWithPasswordSkip',
            variant: 'secondary',
            onClick: handleSkip,
            children: t('Skip this step'),
            processing: working,
            disabled: disabled,
            sx: styleProps.actionButton,
          }),
          {
            id: 'verificationWithPasswordConfirm',
            variant: 'primary',
            onClick: handleSubmit,
            children: working ? t('Setting up...') : t('Confirm'),
            processing: working,
            disabled: disabled,
            sx: styleProps.actionButton,
          },
        ].filter(Boolean)}
      >
        <Box sx={styleProps.inputFieldContainer}>
          <TextInput // Password Field
            type={isPasswordVisible ? 'text' : 'password'}
            name="password"
            label={t('Create Password')}
            value={formValues.password || ''}
            onChange={(e) => handleInputChange('password', e.target.value)}
            disabled={disabled}
            error={validationErrors.password}
            sx={styleProps.passwordInputField}
          />
          <Box sx={styleProps.visibilityIconContainer}>
            <Icon
              id="password-view-toggle"
              variant="default"
              icon={isPasswordVisible ? VisibilityOffIcon : VisibilityIcon}
              sx={styleProps.visibilityIcon}
              label={t('Toggle visibility')}
              onClick={() => setIsPasswordVisible(isVisible => !isVisible)}
            />
          </Box>
        </Box>

        <Box sx={styleProps.inputFieldContainer}>
          <TextInput // Confirm Password Field
            type={isPasswordConfirmVisible ? 'text' : 'password'}
            name="passwordConfirm"
            label={t('Confirm password')}
            value={formValues.passwordConfirm || ''}
            onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
            disabled={disabled}
            error={validationErrors.passwordConfirm}
            sx={styleProps.passwordInputField}
          />
          <Box sx={styleProps.visibilityIconContainer}>
            <Icon
              id="password-view-toggle-confirm"
              variant="default"
              icon={isPasswordConfirmVisible ? VisibilityOffIcon : VisibilityIcon}
              sx={styleProps.visibilityIcon}
              label={t('Toggle visibility')}
              onClick={() => setIsPasswordConfirmVisible(isVisible => !isVisible)}
            />
          </Box>
        </Box>

        <Box sx={styleProps.inputFieldContainer}>
          <InputMask // Birthdate Field
            mask="99/99/9999"
            maskPlaceholder="mm/dd/yyyy"
            value={(formValues.birthday || '').replace(dateRegex, '$2/$3/$1')}
            onChange={(e) => handleInputChange('birthday', e.target.value.replace(dateRegex, '$3-$1-$2'))}
            onBlur={(e) => handleInputChange('birthday', e.target.value.replace(dateRegex, '$3-$1-$2'))}
            disabled={disabled}
          >
            <TextInput
              name="birthday"
              label={t('Birthdate - This should match the birthdate your clinician has on record')}
              placeholder="mm/dd/yyyy"
              variant="condensed"
              error={validationErrors.birthday}
              width="100%"
            />
          </InputMask>
        </Box>

        <Box sx={styleProps.notificationContainer}>
          <Notification notification={notification || propsNotification}/>
        </Box>
      </Container>
    </Box>
  );
};

VerificationWithPassword.propTypes = {
  api: PropTypes.object.isRequired,
  fetchingUser: PropTypes.bool,
  user: PropTypes.object,
};

export default VerificationWithPassword;
